import React from 'react';
import PropTypes from 'prop-types';
import { default as ReactSelect } from './react-select';
import SelectValue from './select-value';
import SelectMultiValue from './select-multi-value';
import SelectOption from './select-option';
import SelectPlaceholder from './select-placeholder';
import View from '@loggi/ui/components/view';
import Display from '@loggi/ui/components/display';
import classNames from 'classnames';
import * as CONST from './constants';
import Styles from './select.scss';
import Spinner from '@loggi/ui/components/spinner';
import InputLabel from '@loggi/ui/components/input-label';
import InputStatus from '@loggi/ui/components/input-status';
import isString from 'lodash/isString';

export default class Select extends React.Component {
  static displayName = 'Select';

  static propTypes = {
    autoHeight: PropTypes.bool,
    disabled: PropTypes.bool,
    size: PropTypes.string,
    tabIndex: PropTypes.number,
    border: PropTypes.bool,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    hideArrow: PropTypes.bool,
    isLoading: PropTypes.bool,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    searchPlaceholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    placeholderIcon: PropTypes.string,
    initialValue: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    onSearchChange: PropTypes.func,
    filterOptions: PropTypes.func,
    opened: PropTypes.bool,
    multi: PropTypes.bool,
    optionRenderer: PropTypes.func,
    options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    placeholderRenderer: PropTypes.func,
    searchable: PropTypes.bool,
    searchCount: PropTypes.number,
    valueRenderer: PropTypes.func,
    labelComponent: PropTypes.object,
    labelComponentProps: PropTypes.object
  };

  static defaultProps = {
    autoHeight: false,
    searchable: false,
    clearable: false,
    border: true,
    searchingText: 'Searching...',
    noResultsText: 'No results found',
    tabIndex: 0,
    valueRenderer: SelectValue,
    optionRenderer: SelectOption,
    placeholderRenderer: SelectPlaceholder,
    labelComponent: null,
    labelComponentProps: null,

    // Only for multi,
    multiValueRenderer: SelectMultiValue,
    singularLabel: 'item selecionado',
    pluralLabel: 'itens selecionados'
  };
  constructor(props) {
    super(props);
    const { initialValue, multi } = this.props;
    this.state = {
      value: multi && !initialValue ? [] : initialValue
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { initialValue } = nextProps;
    if (initialValue !== this.state.value) {
      this.setState({
        value: initialValue
      });
    }
  }

  componentDidMount() {
    this._checkOpened();
  }

  componentDidUpdate() {
    this._checkOpened();
  }

  componentWillUnmount() {
    clearInterval(this.timeout);
  }

  _onChange(value, event) {
    const { onChange, multi } = this.props;

    if (multi) {
      let values = [];

      if (isString(this.state.value)) {
        values.push(this.state.value);
      } else {
        values = this.state.value || [];
      }

      if (!values.includes(value)) {
        values.push(value);
      } else {
        values.splice(values.indexOf(value), 1);
      }

      value = values;
    }

    this.setState({
      value: value
    });

    onChange(value, event);
  }

  _checkOpened() {
    this.timeout = setTimeout(() => {
      if (this.props.opened && this.refs && this.refs.select) {
        if (!this.refs.select.state.isOpen) {
          this.refs.select.setState({ isOpen: true });
          this.refs.select.focus();
        }
      }
    });
  }

  render() {
    const {
      disabled,
      autoHeight,
      options,
      placeholder,
      placeholderIcon,
      searchable,
      size,
      tabIndex,
      isLoading,
      label,
      helperText,
      statusText,
      status,
      labelComponent,
      labelComponentProps
    } = this.props;

    const isAsync = typeof options === 'function';
    const placeholderItem = this.props.placeholderRenderer({
      label: placeholder,
      icon: placeholderIcon
    });
    const Component = isAsync ? ReactSelect.Async : ReactSelect;
    const onChange = this._onChange.bind(this);
    const { value } = this.state;
    const selectClasses = classNames([
      Styles.Select,
      { 'Select-menu-wrap': autoHeight },
      { [`form-control-${size}`]: size },
      { 'hide-arrow': this.props.clearable || this.props.hideArrow },
      { 'select-no-border': !this.props.border },
      this.props.className
    ]);

    let selectProps = {};

    if (isAsync) {
      selectProps = {
        autoload: false,
        loadOptions: options,
        clearable: this.props.clearable,
        placeholder: placeholderItem,
        filterOptions: items => items,
        loadingPlaceholder: this.props.searchingText,
        searchingText: this.props.searchingText,
        noResultsText: this.props.noResultsText,
        onInputChange: this.props.onSearchChange,
        cacheAsyncResults: false,
        backspaceRemoves: false
      };
    } else {
      selectProps = Object.assign(CONST.reactSelectBaseProps, {
        options: options,
        optionsCount: options ? options.length : 0,
        placeholder: placeholderItem,
        clearable: this.props.clearable,
        onInputChange: this.props.onSearchChange,
        filterOptions: this.props.filterOptions,
        disabled
      });

      if (this.props.multi) {
        const selectedOptions = [];
        let optionsCount = 0;
        const mapMultiOptions = multiOptions => {
          return multiOptions.map(option => {
            option.isMulti = true;
            option.selected = (this.state.value || []).includes(option.value);
            if (option.selected) {
              selectedOptions.push(option);
            }
            if (option.options && option.options.length > 0) {
              option.options = mapMultiOptions(option.options);
            } else {
              optionsCount++;
            }
            return option;
          });
        };

        selectProps.options = mapMultiOptions(options);
        selectProps.optionsCount = optionsCount;

        if (value && value.length > 0) {
          // Since react-select will never match one of our values,
          // we'll force the use of value renderer on placeholder
          // when have at least one value selected
          selectProps.placeholder = this.props.multiValueRenderer({
            selectedCount: selectedOptions.length,
            selectedOptions: selectedOptions,
            singularLabel: this.props.singularLabel,
            pluralLabel: this.props.pluralLabel,
            allSelectedLabel:
              this.props.allSelectedLabel || this.props.placeholder,
            optionsCount: optionsCount
          });
        }
      }
    }

    selectProps.inputProps = {
      tabIndex: tabIndex
    };
    const hasGroups = selectProps.options
      ? selectProps.options.find(
          option => option.options && option.options.length > 0
        )
      : false;

    if (hasGroups) {
      selectProps.options.forEach((option, index) => {
        if (index > 0) {
          option.divider = true;
        }
      });
    }

    return (
      <View className={selectClasses}>
        <Display when={Boolean(label || labelComponent)}>
          <View>
            <InputLabel
              text={label}
              helperText={helperText}
              disabled={disabled}
              component={labelComponent}
              componentProps={labelComponentProps}
            />
          </View>
        </Display>
        <Component
          ref="select"
          {...selectProps}
          onChange={onChange}
          onClose={this.props.onClose}
          onOpen={this.props.onOpen}
          optionRenderer={this.props.optionRenderer}
          searchPlaceholder={this.props.searchPlaceholder}
          searchable={searchable}
          searchCount={this.props.searchCount}
          value={this.props.multi ? null : value}
          valueRenderer={this.props.valueRenderer}
          arrowRenderer={isLoading ? () => <Spinner /> : null}
        />
        <InputStatus text={statusText} status={status} disabled={disabled} />
      </View>
    );
  }
}
